// Geospatial helpers ported from the iOS app: MKCoordinateRegion math
// (GaugeManager, StationManager, CombinedMapView), alert geometry stats
// (NWSAlert.computeGeometry) and ray-casting hit tests (PolygonContains).
import type { Geometry, MultiPolygon, Polygon, Position } from 'geojson';
import { METERS_PER_DEGREE_LAT } from '../config/constants';

export interface LatLng {
  lat: number;
  lng: number;
}

/** MKCoordinateRegion equivalent: a center plus latitude/longitude spans in degrees. */
export interface Region {
  center: LatLng;
  latDelta: number;
  lonDelta: number;
}

export interface BBox {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

export function regionToBBox(r: Region): BBox {
  return {
    minLat: r.center.lat - r.latDelta / 2,
    maxLat: r.center.lat + r.latDelta / 2,
    minLon: r.center.lng - r.lonDelta / 2,
    maxLon: r.center.lng + r.lonDelta / 2,
  };
}

export function bboxToRegion(b: BBox): Region {
  return {
    center: { lat: (b.minLat + b.maxLat) / 2, lng: (b.minLon + b.maxLon) / 2 },
    latDelta: b.maxLat - b.minLat,
    lonDelta: b.maxLon - b.minLon,
  };
}

/** MKCoordinateRegion(center:latitudinalMeters:longitudinalMeters:). */
export function regionAround(center: LatLng, latMeters: number, lonMeters = latMeters): Region {
  const latDelta = latMeters / METERS_PER_DEGREE_LAT;
  const cos = Math.max(0.01, Math.cos((center.lat * Math.PI) / 180));
  const lonDelta = lonMeters / (111_320 * cos);
  return { center, latDelta, lonDelta };
}

/** Grows both spans by a multiplier, clamped to the globe (GaugeManager.paddedRegion). */
export function scaledRegion(r: Region, multiplier: number): Region {
  return {
    center: r.center,
    latDelta: Math.min(180, r.latDelta * multiplier),
    lonDelta: Math.min(360, r.lonDelta * multiplier),
  };
}

/** Adds `fraction` of each span on every side (visibleRegionPaddingFraction). */
export function paddedRegion(r: Region, fraction: number): Region {
  return scaledRegion(r, 1 + 2 * fraction);
}

export function regionContainsRegion(outer: Region, inner: Region): boolean {
  const o = regionToBBox(outer);
  const i = regionToBBox(inner);
  return i.minLat >= o.minLat && i.maxLat <= o.maxLat && i.minLon >= o.minLon && i.maxLon <= o.maxLon;
}

export function regionContainsPoint(r: Region, p: LatLng): boolean {
  const b = regionToBBox(r);
  return p.lat >= b.minLat && p.lat <= b.maxLat && p.lng >= b.minLon && p.lng <= b.maxLon;
}

export function bboxIntersects(a: BBox, b: BBox): boolean {
  return a.maxLat >= b.minLat && a.minLat <= b.maxLat && a.maxLon >= b.minLon && a.minLon <= b.maxLon;
}

export function bboxContainsPoint(b: BBox, p: LatLng): boolean {
  return p.lat >= b.minLat && p.lat <= b.maxLat && p.lng >= b.minLon && p.lng <= b.maxLon;
}

export function distanceSquaredDeg(a: LatLng, b: LatLng): number {
  const dLat = a.lat - b.lat;
  const dLon = a.lng - b.lng;
  return dLat * dLat + dLon * dLon;
}

/**
 * Rings the iOS NWSGeometry decoder produces: every ring of a Polygon, and the
 * outer ring of each member of a MultiPolygon (NWSAlert.swift 30-55).
 */
export function alertRings(geometry: Geometry | null | undefined): Position[][] {
  if (!geometry) return [];
  if (geometry.type === 'Polygon') return geometry.coordinates.filter((ring) => ring.length > 0);
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.map((poly) => poly[0]).filter((ring): ring is Position[] => Array.isArray(ring) && ring.length > 0);
  }
  return [];
}

export function geometryBBox(geometry: Geometry | null | undefined): BBox | null {
  const rings = allRings(geometry);
  let box: BBox | null = null;
  for (const ring of rings) {
    for (const [lng, lat] of ring) {
      if (!box) box = { minLat: lat, maxLat: lat, minLon: lng, maxLon: lng };
      else {
        if (lat < box.minLat) box.minLat = lat;
        if (lat > box.maxLat) box.maxLat = lat;
        if (lng < box.minLon) box.minLon = lng;
        if (lng > box.maxLon) box.maxLon = lng;
      }
    }
  }
  return box;
}

function allRings(geometry: Geometry | null | undefined): Position[][] {
  if (!geometry) return [];
  if (geometry.type === 'Polygon') return geometry.coordinates;
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat();
  return [];
}

/** Drops a duplicated closing vertex (Array+Ext.swift normalizedRing). */
export function normalizedRing(ring: Position[]): Position[] {
  if (ring.length > 1) {
    const [f, l] = [ring[0], ring[ring.length - 1]];
    if (f[0] === l[0] && f[1] === l[1]) return ring.slice(0, -1);
  }
  return ring;
}

/** Shoelace area in square degrees (NWSAlert.swift 199-212). */
export function shoelaceArea(ring: Position[]): number {
  const r = normalizedRing(ring);
  if (r.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < r.length; i++) {
    const [x1, y1] = r[i];
    const [x2, y2] = r[(i + 1) % r.length];
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) * 0.5;
}

export interface GeometryStats {
  centroid: LatLng | null;
  bbox: BBox | null;
  area: number;
}

/**
 * Centroid (mean of every vertex), bounding box and summed ring area, computed
 * once per alert exactly as NWSAlert.computeGeometry does.
 */
export function alertGeometryStats(rings: Position[][]): GeometryStats {
  let count = 0;
  let sumLat = 0;
  let sumLon = 0;
  let bbox: BBox | null = null;
  for (const ring of rings) {
    for (const [lng, lat] of ring) {
      count++;
      sumLat += lat;
      sumLon += lng;
      if (!bbox) bbox = { minLat: lat, maxLat: lat, minLon: lng, maxLon: lng };
      else {
        if (lat < bbox.minLat) bbox.minLat = lat;
        else if (lat > bbox.maxLat) bbox.maxLat = lat;
        if (lng < bbox.minLon) bbox.minLon = lng;
        else if (lng > bbox.maxLon) bbox.maxLon = lng;
      }
    }
  }
  if (count === 0) return { centroid: null, bbox: null, area: 0 };
  const area = rings.reduce((acc, ring) => acc + shoelaceArea(ring), 0);
  return { centroid: { lat: sumLat / count, lng: sumLon / count }, bbox, area };
}

/** Ray-casting point in ring test (PolygonContains.swift). */
export function ringContains(ring: Position[], p: LatLng): boolean {
  if (ring.length < 3) return false;
  let inside = false;
  let j = ring.length - 1;
  for (let i = 0; i < ring.length; i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > p.lat !== yj > p.lat && p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
    j = i;
  }
  return inside;
}

export function isPolygonal(g: Geometry | null | undefined): g is Polygon | MultiPolygon {
  return !!g && (g.type === 'Polygon' || g.type === 'MultiPolygon');
}
